import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton.jsx";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import moment from "moment";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice.js";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";



const VOUCHER_SORT_FIELDS = {
  Voucher_ID: "Voucher_ID",
  Voucher_Name: "Voucher_Name",
  Voucher_DiscountPercentage: "Voucher_DiscountPercentage",
};

const VOUCHER_SORT_LABELS = {
  [VOUCHER_SORT_FIELDS.Voucher_ID]: "Voucher ID",
  [VOUCHER_SORT_FIELDS.Voucher_Name]: "Name",
  [VOUCHER_SORT_FIELDS.Voucher_DiscountPercentage]: "Discount %",
};

const ManageVoucher = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const vouchersFetchSeqRef = useRef(0);
  const [statusConfirmVoucher, setStatusConfirmVoucher] = useState(null);

  const pageKey = "vouchers";
  const defaultVisibleColumns = {
    Voucher_Name: true,
    Voucher_DiscountPercentage: true,
    Voucher_Code: true,
    Voucher_MinOrderTotal: true,
    Voucher_NoOfUses: true,
    Voucher_MaxUsesLimit: true,
    Voucher_StartDate: true,
    Voucher_EndDate: true,
    Voucher_Active: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Voucher_ID", label: "Voucher ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const SHOW_BULK_ACTIONS = false;

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;
  const formatDate = (dateVal) => {
    if (dateVal == null || dateVal === "") return "—";
    try {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const navigate = useNavigate();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: VOUCHER_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const fetchVouchers = useCallback(async () => {
    const fetchSeq = ++vouchersFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_VOUCHERS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
          PageNumber: pageNumber,
          PageSize: parseInt(pageSize.value, 10),
        },
      });

      if (fetchSeq !== vouchersFetchSeqRef.current) return;

      const voucherData = response.data.data || response.data;
      const rawList = Array.isArray(voucherData) ? voucherData : [];
      const records =
        rawList.length > 0
          ? Number(rawList[0].TotalRecord ?? rawList[0].totalRecord ?? rawList.length)
          : 0;
      const pages = Math.max(1, Math.ceil(records / Number(pageSize.value)));

      const normalized = rawList.map((v) => ({
        Voucher_ID: v?.Voucher_ID ?? v?.voucher_ID ?? v?.voucherId ?? v?.id ?? 0,
        Voucher_Name: v?.Voucher_Name ?? v?.voucher_Name ?? v?.voucherName ?? "",
        Voucher_DiscountPercentage:
          v?.Voucher_DiscountPercentage ?? v?.voucher_DiscountPercentage ?? v?.discountPercentage ?? 0,
        Voucher_Code: v?.Voucher_Code ?? v?.voucher_Code ?? v?.voucherCode ?? "",
        Voucher_MinimumOrderTotalAmount:
          v?.Voucher_MinimumOrderTotalAmount ?? v?.voucher_MinimumOrderTotalAmount ?? v?.minimumOrderTotalAmount ?? 0,
        Voucher_NoOfUses: v?.Voucher_NoOfUses ?? v?.voucher_NoOfUses ?? v?.noOfUses ?? 0,
        Voucher_MaxUsesLimit: v?.Voucher_MaxUsesLimit ?? v?.voucher_MaxUsesLimit ?? v?.maxUsesLimit ?? 0,
        Voucher_StartDate: v?.Voucher_StartDate ?? v?.voucher_StartDate ?? v?.startDate ?? null,
        Voucher_EndDate: v?.Voucher_EndDate ?? v?.voucher_EndDate ?? v?.endDate ?? null,
        Voucher_Active: v?.Voucher_Active ?? v?.voucher_Active ?? v?.active ?? false,
        Voucher_Detail: v?.Voucher_Detail ?? v?.voucher_Detail ?? v?.voucherDetail ?? "",
        MultiAllow_Active: v?.MultiAllow_Active ?? v?.multiAllow_Active ?? false,
        Voucher_IsMobileActive:
          v?.Voucher_IsMobileActive ?? v?.voucher_IsMobileActive ?? false,
      }));

      setVouchers(normalized);
      setTotalRecords(records);
      setTotalPages(pages);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== vouchersFetchSeqRef.current) return;
      setVouchers([]);
      setTotalRecords(0);
      setTotalPages(0);
      showErrorToast("Failed to load vouchers.");
    } finally {
      if (fetchSeq === vouchersFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(vouchersFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchVouchers(), [fetchVouchers]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value, pageNumber, pageSize?.value]);

  const hasFiltersApplied = () => search.trim() !== "";

  const showEmptyCatalogState =
    initialLoadComplete && !loading && !hasFiltersApplied() && catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No vouchers match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no vouchers to show right now.";
  };

  const renderNoDataMessage = () => {
    if (loading || vouchers.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No vouchers found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const handleAddVoucher = () => navigate("/voucher/addvoucher");

  const handleRowClick = (voucherId) => {
    navigate(`/voucher/updateVoucher/${voucherId}`);
  };

  const buildVoucherUpdatePayload = (voucher, active) => ({
    Voucher_ID: voucher.Voucher_ID,
    CompID,
    Voucher_Name: voucher.Voucher_Name,
    Voucher_Code: voucher.Voucher_Code,
    Voucher_Detail: voucher.Voucher_Detail ?? "",
    Voucher_DiscountPercentage: parseFloat(voucher.Voucher_DiscountPercentage) || 0,
    Voucher_MinimumOrderTotalAmount:
      parseFloat(voucher.Voucher_MinimumOrderTotalAmount) || 0,
    Voucher_StartDate: voucher.Voucher_StartDate
      ? moment(voucher.Voucher_StartDate).format()
      : moment().format(),
    Voucher_EndDate: voucher.Voucher_EndDate
      ? moment(voucher.Voucher_EndDate).format()
      : moment().format(),
    Voucher_MaxUsesLimit: parseInt(voucher.Voucher_MaxUsesLimit, 10) || 0,
    Voucher_NoOfUses: parseInt(voucher.Voucher_NoOfUses, 10) || 0,
    Voucher_Active: active,
    UpdateDateTime: new Date().toISOString(),
    MultiAllow_Active: voucher.MultiAllow_Active ?? false,
    Voucher_IsMobileActive: voucher.Voucher_IsMobileActive ?? false,
  });

  const confirmVoucherStatusChange = async () => {
    if (!statusConfirmVoucher) return;

    const { Voucher_ID, Voucher_Active, Voucher_Name } = statusConfirmVoucher;
    const newActive = !Voucher_Active;

    setVouchers((prev) =>
      prev.map((v) =>
        v.Voucher_ID === Voucher_ID ? { ...v, Voucher_Active: newActive } : v
      )
    );
    setStatusConfirmVoucher(null);

    try {
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_VOUCHER,
        buildVoucherUpdatePayload(statusConfirmVoucher, newActive)
      );

      if (!response.data.status) {
        setVouchers((prev) =>
          prev.map((v) =>
            v.Voucher_ID === Voucher_ID ? { ...v, Voucher_Active } : v
          )
        );
        showErrorToast(response.data.Message || "Failed to update voucher status.");
        return;
      }

      showSuccessToast(
        `"${Voucher_Name || "Voucher"}" is now ${newActive ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setVouchers((prev) =>
        prev.map((v) =>
          v.Voucher_ID === Voucher_ID ? { ...v, Voucher_Active } : v
        )
      );
      showErrorToast("Failed to update voucher status.");
    }
  };

  const handleDeleteVoucher = async (voucherItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete voucher ${voucherItem.Voucher_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!voucherItem.Voucher_ID || !CompID) {
        showErrorToast("Invalid voucher or company ID.");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_VOUCHER, {
        params: {
          CompID,
          id: voucherItem.Voucher_ID,
        },
      });

      if (response.data.status) {
        showSuccessToast("Voucher deleted successfully.");
        fetchVouchers();
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the voucher.");
      setLoading(false);
    }
  };

  const handleSelectItem = (voucherId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (vouchers[i]?.Voucher_ID) {
            newSelected.add(vouchers[i].Voucher_ID);
          }
        }
      } else {
        if (newSelected.has(voucherId)) {
          newSelected.delete(voucherId);
        } else {
          newSelected.add(voucherId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(vouchers.map((item) => item.Voucher_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No vouchers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected vouchers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((voucherId) =>
          axiosInstance.delete(APIs.DELETE_VOUCHER, {
            params: {
              CompID,
              id: voucherId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchVouchers();
      showSuccessToast(`${selectedItems.size} vouchers deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected vouchers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No vouchers selected for editing.");
      return;
    }

    const selectedVoucherIds = Array.from(selectedItems);
    navigate(`/voucher/bulkEdit`, { state: { selectedVoucherIds } });
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Vouchers</li>
          </ol>
          <h1 className="page-header mb-0">Manage Vouchers</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={handleAddVoucher}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Voucher
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddVoucher} title="Add Voucher" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Vouchers"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by voucher name, code..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                columnOrder={[
                  "Voucher_Name",
                  "Voucher_DiscountPercentage",
                  "Voucher_Code",
                  "Voucher_MinOrderTotal",
                  "Voucher_NoOfUses",
                  "Voucher_MaxUsesLimit",
                  "Voucher_StartDate",
                  "Voucher_EndDate",
                  "Voucher_Active",
                ]}
                columnLabels={{
                  Voucher_Name: "Name",
                  Voucher_DiscountPercentage: "Discount %",
                  Voucher_Code: "Code",
                  Voucher_MinOrderTotal: "Min. Order",
                  Voucher_NoOfUses: "Times Used",
                  Voucher_MaxUsesLimit: "Max Uses",
                  Voucher_StartDate: "Start Date",
                  Voucher_EndDate: "End Date",
                  Voucher_Active: "Status",
                }}
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} voucher{selectedItems.size > 1 ? "s" : ""} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                      >
                        <i className="fa fa-trash me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && vouchers.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  vouchers.length > 0 &&
                                  selectedItems.size === vouchers.length
                                }
                              />
                            </th>
                          )}
                          {visibleColumns.Voucher_Name && (
                            <SortableColumnHeader
                              sortField={VOUCHER_SORT_FIELDS.Voucher_Name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.Voucher_DiscountPercentage && (
                            <SortableColumnHeader
                              sortField={VOUCHER_SORT_FIELDS.Voucher_DiscountPercentage}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Discount %
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.Voucher_Code && (
                            <th className="pt-2 pb-2 text-start">Code</th>
                          )}
                          {visibleColumns.Voucher_MinOrderTotal && (
                            <th className="pt-2 pb-2 text-center">Min. Order</th>
                          )}
                          {visibleColumns.Voucher_NoOfUses && (
                            <th className="pt-2 pb-2 text-center">Times Used</th>
                          )}
                          {visibleColumns.Voucher_MaxUsesLimit && (
                            <th className="pt-2 pb-2 text-center">Max Uses</th>
                          )}
                          {visibleColumns.Voucher_StartDate && (
                            <th className="pt-2 pb-2 text-start">Start Date</th>
                          )}
                          {visibleColumns.Voucher_EndDate && (
                            <th className="pt-2 pb-2 text-start">End Date</th>
                          )}
                          {visibleColumns.Voucher_Active && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-start">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vouchers.map((voucher, index) => (
                          <tr
                            key={voucher.Voucher_ID}
                            onClick={(e) => {
                              if (
                                e.target.closest(".status-badge") ||
                                e.target.closest(".dropdown")
                              ) {
                                return;
                              }
                              handleRowClick(voucher.Voucher_ID);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(voucher.Voucher_ID)}
                                  onChange={(e) =>
                                    handleSelectItem(voucher.Voucher_ID, index, e)
                                  }
                                />
                              </td>
                            )}
                            {visibleColumns.Voucher_Name && (
                              <td className="align-middle text-start">{voucher.Voucher_Name}</td>
                            )}
                            {visibleColumns.Voucher_DiscountPercentage && (
                              <td className="align-middle text-center">
                                {voucher.Voucher_DiscountPercentage}%
                              </td>
                            )}
                            {visibleColumns.Voucher_Code && (
                              <td className="align-middle text-start">{voucher.Voucher_Code}</td>
                            )}
                            {visibleColumns.Voucher_MinOrderTotal && (
                              <td className="align-middle text-center">
                                {getCurrencySymbol(compCurrency)}
                                {(Number(voucher.Voucher_MinimumOrderTotalAmount) || 0).toFixed(2)}
                              </td>
                            )}
                            {visibleColumns.Voucher_NoOfUses && (
                              <td className="align-middle text-center">
                                {voucher.Voucher_NoOfUses ?? 0}
                              </td>
                            )}
                            {visibleColumns.Voucher_MaxUsesLimit && (
                              <td className="align-middle text-center">
                                {voucher.Voucher_MaxUsesLimit ?? 0}
                              </td>
                            )}
                            {visibleColumns.Voucher_StartDate && (
                              <td className="align-middle text-start">
                                {formatDate(voucher.Voucher_StartDate)}
                              </td>
                            )}
                            {visibleColumns.Voucher_EndDate && (
                              <td className="align-middle text-start">
                                {formatDate(voucher.Voucher_EndDate)}
                              </td>
                            )}
                            {visibleColumns.Voucher_Active && (
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <StatusBadge
                                  isActive={voucher.Voucher_Active}
                                  onClick={() => setStatusConfirmVoucher(voucher)}
                                />
                              </td>
                            )}
                            <td
                              className="align-middle text-start"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TableActionsMenu dropup menuStyle={{ zIndex: 9999 }}>
                                <TableActionsItem
                                  as={Link}
                                  to={`/voucher/updateVoucher/${voucher.Voucher_ID}`}
                                  icon="fa fa-edit"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteVoucher(voucher)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change voucher status?"
        subtitle={
          statusConfirmVoucher
            ? `Are you sure you want to set "${statusConfirmVoucher.Voucher_Name || "this voucher"}" to ${
                statusConfirmVoucher.Voucher_Active ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmVoucher(null)}
        isOpen={!!statusConfirmVoucher}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmVoucherStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageVoucher;
